/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

/**
 * Fresh Miami Flowers homepage enhancement:
 * - Desktop: show the Custom Florist Designed Bouquet description below its image.
 * - Mobile: add a dedicated Custom Florist Designed Bouquet feature immediately
 *   after the Luxury Flowers section.
 */
const FMF_CUSTOM_BOUQUET_HANDLE = 'custom-florist-designed-bouquet';
let fmfCustomBouquetPromise;

function fmfEscapeHtml(value = '') {
  return String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  }[character]));
}

function fmfCustomBouquetExcerpt(html = '', maxLength = 220) {
  const holder = document.createElement('div');
  holder.innerHTML = html;
  const text = (holder.textContent || '').replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim().replace(/[,\s;:.-]+$/, '')}…`;
}

function fmfCustomBouquetMoney(cents) {
  const amount = Number(cents || 0) / 100;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
}

function fmfLoadCustomBouquet() {
  if (!fmfCustomBouquetPromise) {
    fmfCustomBouquetPromise = fetch(`/products/${FMF_CUSTOM_BOUQUET_HANDLE}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        console.warn('[FMF] Custom florist homepage feature could not load.', error);
        return null;
      });
  }
  return fmfCustomBouquetPromise;
}

function fmfEnsureCustomBouquetStyles() {
  if (document.getElementById('FmfCustomBouquetHomepageStyles')) return;

  const style = document.createElement('style');
  style.id = 'FmfCustomBouquetHomepageStyles';
  style.textContent = `
    #FmfDesktopCustomBouquetDescription {
      margin-top: 2rem;
      padding: 0 0.4rem;
      font-size: 1.55rem;
      line-height: 1.65;
      color: rgba(var(--color-foreground), 0.82);
    }
    #FmfDesktopCustomBouquetDescription > :first-child { margin-top: 0; }
    #FmfDesktopCustomBouquetDescription > :last-child { margin-bottom: 0; }

    .fmf-custom-bouquet-mobile { display: none; }

    @media screen and (max-width: 749px) {
      #FmfDesktopCustomBouquetDescription { display: none !important; }

      .fmf-custom-bouquet-mobile {
        display: block;
        background: #fff;
        text-align: center;
      }
      .fmf-custom-bouquet-mobile__kicker {
        margin: 0 0 0.5rem;
        font-size: 1.2rem;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      .fmf-custom-bouquet-mobile__title {
        margin: 0 0 1.4rem;
        font-size: 2.4rem;
        line-height: 1.2;
      }
      .fmf-custom-bouquet-mobile__image-link {
        display: block;
        overflow: hidden;
        border-radius: 1.4rem;
        background: #f6f6f6;
      }
      .fmf-custom-bouquet-mobile__image {
        display: block;
        width: 100%;
        height: auto;
        aspect-ratio: 1 / 1;
        object-fit: cover;
      }
      .fmf-custom-bouquet-mobile__price {
        margin-top: 1.5rem;
        font-size: 2rem;
        font-weight: 700;
      }
      .fmf-custom-bouquet-mobile__description {
        margin: 1rem auto 1.6rem;
        max-width: 34rem;
        font-size: 1.45rem;
        line-height: 1.55;
      }
      .fmf-custom-bouquet-mobile__button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 4.8rem;
        padding: 1.2rem 2.4rem;
        border: 0;
        border-radius: 0.6rem;
        background: #111;
        color: #fff;
        font-size: 1.45rem;
        font-weight: 700;
        text-decoration: none;
      }
    }

    @media screen and (min-width: 750px) {
      .fmf-custom-bouquet-mobile { display: none !important; }
    }
  `;
  document.head.appendChild(style);
}

function fmfAddDesktopCustomBouquetDescription(main, product) {
  if (!window.matchMedia('(min-width: 750px)').matches) return;

  const featuredProduct = Array.from(main.querySelectorAll('product-info')).find((element) => {
    const url = element.dataset.url || '';
    return url.includes(`/products/${FMF_CUSTOM_BOUQUET_HANDLE}`);
  });
  const mediaWrapper = featuredProduct?.querySelector('.product__media-wrapper');
  if (!mediaWrapper || mediaWrapper.querySelector('#FmfDesktopCustomBouquetDescription')) return;

  const description = document.createElement('div');
  description.id = 'FmfDesktopCustomBouquetDescription';
  description.className = 'rte fmf-custom-bouquet-description';
  description.innerHTML = product.description || '';
  mediaWrapper.appendChild(description);
}

function fmfAddMobileCustomBouquet(main, product) {
  if (!window.matchMedia('(max-width: 749px)').matches) return;
  if (main.querySelector('#FmfMobileCustomBouquet')) return;

  const luxurySection = main.querySelector(
    '.fmf-mobile-home .fmf-section--products.fmf-section--catalog:not(.fmf-collection-switcher)'
  );
  if (!luxurySection) return;

  const productUrl = product.url || `/products/${FMF_CUSTOM_BOUQUET_HANDLE}`;
  const title = product.title || 'Custom Florist Designed Bouquet';
  const excerpt = fmfCustomBouquetExcerpt(product.description || '');
  const image = product.featured_image || product.images?.[0] || '';
  const priceLabel = product.price_varies
    ? `From ${fmfCustomBouquetMoney(product.price_min ?? product.price)}`
    : fmfCustomBouquetMoney(product.price);

  const section = document.createElement('div');
  section.id = 'FmfMobileCustomBouquet';
  section.className = 'fmf-section fmf-custom-bouquet-mobile';
  section.setAttribute('aria-label', title);

  section.innerHTML = `
    <div class="fmf-custom-bouquet-mobile__kicker">Made just for them</div>
    <h2 class="fmf-custom-bouquet-mobile__title">${fmfEscapeHtml(title)}</h2>
    ${image ? `
      <a class="fmf-custom-bouquet-mobile__image-link" href="${fmfEscapeHtml(productUrl)}">
        <img class="fmf-custom-bouquet-mobile__image" src="${fmfEscapeHtml(image)}" alt="${fmfEscapeHtml(title)}" loading="lazy">
      </a>
    ` : ''}
    <div class="fmf-custom-bouquet-mobile__price">${fmfEscapeHtml(priceLabel)}</div>
    ${excerpt ? `<p class="fmf-custom-bouquet-mobile__description">${fmfEscapeHtml(excerpt)}</p>` : ''}
    <a class="fmf-custom-bouquet-mobile__button" href="${fmfEscapeHtml(productUrl)}">DESIGN MY BOUQUET</a>
  `;

  luxurySection.after(section);
}

async function fmfEnhanceCustomBouquetHomepage() {
  const main = document.querySelector('main[data-template="index"]');
  if (!main) return;

  fmfEnsureCustomBouquetStyles();
  const product = await fmfLoadCustomBouquet();
  if (!product) return;

  fmfAddDesktopCustomBouquetDescription(main, product);
  fmfAddMobileCustomBouquet(main, product);
}

function fmfInitCustomBouquetHomepage() {
  fmfEnhanceCustomBouquetHomepage();

  let resizeTimer;
  window.addEventListener('resize', () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(fmfEnhanceCustomBouquetHomepage, 150);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', fmfInitCustomBouquetHomepage, { once: true });
} else {
  fmfInitCustomBouquetHomepage();
}
